import { SendEmailRequest, SendEmailResponse, EmailAttachment } from './types';
import { EmailTemplateService } from './EmailTemplateService';
import { emailConfig } from '../config/emailConfig';
import { fileStorageService } from '../storage';
import { supabase } from '../../shared/services/supabase';
import { netlifyFetch } from '../../shared/services/netlifyFetch';
import { berlinLocalToUtcIso } from '../../shared/utils/berlinTime';

export class EmailService {
  private templateService: EmailTemplateService;

  constructor() {
    this.templateService = new EmailTemplateService();
  }

  /**
   * Intelligenter E-Mail-Versand mit automatischer Storage-Entscheidung
   * ERWEITERT: Unterstützt jetzt Sender-Identitäten für dynamische Signaturen und BCC
   *
   * Stufe 3 (terminierter Versand): Ist request.scheduledAtLocal gesetzt, wird
   * die Mail vollständig gerendert (Template, Signatur, Duz-Anrede,
   * Download-Links für Großdateien) und dann EINGEFROREN statt versendet —
   * Payload nach scheduled_finalization_emails, Anhänge in den Bucket
   * scheduled-email-attachments. Der Minuten-Cron email-scheduled-send
   * verschickt sie zum Zeitpunkt unverändert. Ohne scheduledAtLocal ist das
   * Verhalten byte-identisch zum Bestand.
   */
  async sendEmail(request: SendEmailRequest): Promise<SendEmailResponse> {
    try {
      // Stufe 3: Terminierung validieren, BEVOR irgendetwas verarbeitet oder
      // hochgeladen wird (Berliner Wanduhrzeit → UTC, Vergangenheits-Check).
      let geplanterVersandAmIso: string | null = null;
      if (request.scheduledAtLocal) {
        if (!request.scheduleContext) {
          return { success: false, error: 'Terminierter Versand ohne scheduleContext ist nicht möglich.' };
        }
        try {
          geplanterVersandAmIso = berlinLocalToUtcIso(request.scheduledAtLocal);
        } catch (e) {
          return { success: false, error: e instanceof Error ? e.message : 'Ungültiger Zeitpunkt' };
        }
        if (new Date(geplanterVersandAmIso).getTime() <= Date.now()) {
          return { success: false, error: 'Der geplante Zeitpunkt liegt in der Vergangenheit.' };
        }
      }

      const template = this.templateService.getTemplate(request.templateId);
      if (!template) {
        throw new Error(`Template ${request.templateId} not found`);
      }

      // Smart Attachment Logic (BESTEHENDE FUNKTIONALITÄT BEIBEHALTEN)
      const { attachments, downloadLinks } = await this.processAttachments(request.attachments || []);

      // Erweitere Template-Variablen mit Download-Links UND Sender-Identität
      const enhancedVariables = {
        ...request.variables,
        downloadLinks: downloadLinks,
        // NEUE FUNKTIONALITÄT: Verwende Sender aus Request oder fallback auf bestehende Logik
        senderIdentity: request.senderIdentity || request.variables.senderIdentity
      };

      // Generate templates with enhanced variables (inkl. dynamischer Signatur)
      const subject = template.subject(enhancedVariables);
      const finalBodyHtml = template.bodyHtml(enhancedVariables);
      const finalBodyText = template.bodyText(enhancedVariables);

      // Bereite E-Mail-Payload vor
      const emailPayload: any = {
        to: request.to,
        from: `${emailConfig.sender.name} <${emailConfig.sender.email}>`, // Bleibt unverändert
        replyTo: emailConfig.replyTo, // Bleibt unverändert
        subject,
        html: finalBodyHtml, // Enthält jetzt dynamische Signatur
        text: finalBodyText, // Enthält jetzt dynamische Signatur
        attachments: attachments
      };

      // BCC hinzufügen, wenn vorhanden
      if (request.bcc) {
        emailPayload.bcc = request.bcc;
      }

      // CC hinzufügen, wenn vorhanden
      if (request.cc) {
        emailPayload.cc = request.cc;
      }

      // Stufe 3: Einfrieren statt Senden — das final gerenderte Payload wird
      // 1:1 persistiert; große Dateien sind zu diesem Zeitpunkt bereits als
      // Download-Links im HTML verbaut, eingefroren werden nur echte Anhänge.
      if (geplanterVersandAmIso) {
        return await this.scheduleFrozenEmail(emailPayload, request, geplanterVersandAmIso);
      }

      // Call Netlify function mit verarbeiteten Attachments und optionalem BCC/CC
      const response = await netlifyFetch('/.netlify/functions/send-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(emailPayload)
      });

      if (!response.ok) {
        const error = await response.text();
        throw new Error(`Email send failed: ${error}`);
      }

      const result = await response.json();
      return {
        success: true,
        messageId: result.messageId
      };

    } catch (error) {
      console.error('Email send error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  /**
   * Stufe 3: Persistiert das final gerenderte Payload als eingefrorene Mail.
   * Ablauf: Zeile anlegen (liefert die id für die Storage-Pfade) → Anhänge in
   * den Bucket laden → Anhang-Metadaten an der Zeile hinterlegen. Scheitert
   * ein Schritt, wird vollständig zurückgerollt (kein halbes Paket). Die
   * Vergangenheits-Prüfung in sendEmail garantiert, dass der Cron die Zeile
   * während des Anhang-Uploads nicht claimen kann (Zeitpunkt liegt in der
   * Zukunft).
   */
  private async scheduleFrozenEmail(
    emailPayload: any,
    request: SendEmailRequest,
    geplanterVersandAmIso: string
  ): Promise<SendEmailResponse> {
    const context = request.scheduleContext!;

    // 1. Zeile anlegen (Anhang-Metadaten folgen in Schritt 3)
    const { data: row, error: insertError } = await supabase
      .from('scheduled_finalization_emails')
      .insert({
        customer_id: context.customerId ?? null,
        monat: context.monat,
        jahr: context.jahr,
        to_email: emailPayload.to,
        cc_email: emailPayload.cc ?? null,
        bcc_email: emailPayload.bcc ?? null,
        from_display: emailPayload.from,
        reply_to: emailPayload.replyTo ?? null,
        subject: emailPayload.subject,
        body_html: emailPayload.html,
        body_text: emailPayload.text,
        attachments: [],
        sender_identity_name: request.senderIdentity?.fullName ?? null,
        sender_identity_email: request.senderIdentity?.email ?? null,
        erstellt_von_name: context.erstelltVonName,
        erstellt_von_email: context.erstelltVonEmail,
        versand_status: 'geplant',
        geplanter_versand_am: geplanterVersandAmIso
      })
      .select('id')
      .single();

    if (insertError || !row) {
      return {
        success: false,
        error: `Terminierung fehlgeschlagen: ${insertError?.message ?? 'kein Datensatz angelegt'}`
      };
    }

    // 2. Anhänge in den Bucket laden (Base64 → Blob)
    const attachmentMetas: Array<{
      filename: string;
      content_type: string;
      storage_path: string;
      size_bytes: number;
    }> = [];
    const uploadedPaths: string[] = [];
    const frozenAttachments: EmailAttachment[] = emailPayload.attachments || [];

    for (let i = 0; i < frozenAttachments.length; i++) {
      const att = frozenAttachments[i];
      try {
        const byteCharacters = atob(att.content);
        const byteNumbers = new Array(byteCharacters.length);
        for (let j = 0; j < byteCharacters.length; j++) {
          byteNumbers[j] = byteCharacters.charCodeAt(j);
        }
        const blob = new Blob([new Uint8Array(byteNumbers)], { type: att.contentType });

        // Storage-sicherer Pfad; der Originalname bleibt in den Metadaten erhalten
        const sanitizedFilename = att.filename.replace(/[^a-zA-Z0-9._-]/g, '_');
        const storagePath = `${row.id}/${i}_${sanitizedFilename}`;

        const { error: uploadError } = await supabase.storage
          .from('scheduled-email-attachments')
          .upload(storagePath, blob, { contentType: att.contentType, upsert: false });

        if (uploadError) {
          throw new Error(uploadError.message);
        }

        uploadedPaths.push(storagePath);
        attachmentMetas.push({
          filename: att.filename,
          content_type: att.contentType,
          storage_path: storagePath,
          size_bytes: blob.size
        });
      } catch (e) {
        // Rollback: bereits hochgeladene Anhänge und die Zeile entfernen
        if (uploadedPaths.length > 0) {
          await supabase.storage.from('scheduled-email-attachments').remove(uploadedPaths);
        }
        await supabase.from('scheduled_finalization_emails').delete().eq('id', row.id);
        return {
          success: false,
          error: `Anhang konnte nicht persistiert werden (${att.filename}): ${e instanceof Error ? e.message : 'Unbekannter Fehler'}`
        };
      }
    }

    // 3. Anhang-Metadaten an der Zeile hinterlegen
    const { error: updateError } = await supabase
      .from('scheduled_finalization_emails')
      .update({ attachments: attachmentMetas, updated_at: new Date().toISOString() })
      .eq('id', row.id);

    if (updateError) {
      if (uploadedPaths.length > 0) {
        await supabase.storage.from('scheduled-email-attachments').remove(uploadedPaths);
      }
      await supabase.from('scheduled_finalization_emails').delete().eq('id', row.id);
      return {
        success: false,
        error: `Terminierung fehlgeschlagen: ${updateError.message}`
      };
    }

    console.log(`[EmailService] Mail eingefroren (${row.id}), Versand geplant für ${geplanterVersandAmIso}`);
    return { success: true, scheduled: true };
  }

  /**
   * Verarbeitet Attachments: Kleine als Anhang, große über Storage
   * BESTEHENDE FUNKTIONALITÄT - UNVERÄNDERT BEIBEHALTEN
   */
  private async processAttachments(
    inputAttachments: EmailAttachment[]
  ): Promise<{ attachments: EmailAttachment[], downloadLinks: Array<{ filename: string, url: string }> }> {
    
    if (!inputAttachments.length) {
      return { attachments: [], downloadLinks: [] };
    }

    // Konvertiere Base64 zurück zu File-Objekten für Größenprüfung
    const fileData: Array<{ attachment: EmailAttachment, file: File }> = [];
    
    for (const attachment of inputAttachments) {
      try {
        // Base64 zu File konvertieren für Größenprüfung
        const base64Data = attachment.content;
        const byteCharacters = atob(base64Data);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const file = new File([byteArray], attachment.filename, { type: attachment.contentType });
        
        fileData.push({ attachment, file });
      } catch (error) {
        console.error('Error converting attachment to file:', error);
        // Bei Fehlern Attachment als normalen Anhang behandeln
        fileData.push({ attachment, file: null as any });
      }
    }

    // Prüfe Gesamtgröße aller Dateien
    const allFiles = fileData.map(fd => fd.file).filter(f => f);
    const totalSizeTooBig = fileStorageService.isTotalSizeTooBig(allFiles);

    const finalAttachments: EmailAttachment[] = [];
    const downloadLinks: Array<{ filename: string, url: string }> = [];

    for (const { attachment, file } of fileData) {
      // Entscheide: Anhang oder Storage-Link
      const shouldUseStorage = file && (
        fileStorageService.isFileTooBig(file) || totalSizeTooBig
      );

      if (shouldUseStorage && file) {
        // Upload zu Storage und erstelle Download-Link
        console.log(`Uploading large file to storage: ${file.name} (${(file.size / 1024 / 1024).toFixed(2)}MB)`);
        
        const uploadResult = await fileStorageService.uploadFile(file, file.name);
        
        if (uploadResult.success && uploadResult.publicUrl) {
          downloadLinks.push({
            filename: file.name,
            url: uploadResult.publicUrl
          });
          console.log(`Storage upload successful: ${file.name}`);
        } else {
          // Storage fehlgeschlagen - NICHT als Anhang verwenden bei großen Dateien
          console.error(`Storage upload failed for ${file.name}:`, uploadResult.error);
          
          if (fileStorageService.isFileTooBig(file)) {
            // Datei zu groß für Anhang - kompletter Fehler
            throw new Error(`Datei "${file.name}" ist zu groß (${(file.size / 1024 / 1024).toFixed(2)}MB) und Storage-Upload fehlgeschlagen: ${uploadResult.error}`);
          } else {
            // Nur Gesamtgröße zu groß - Fallback möglich
            console.warn(`Using attachment fallback for ${file.name} despite total size limit`);
            finalAttachments.push(attachment);
          }
        }
      } else {
        // Normale Größe - als Anhang
        finalAttachments.push(attachment);
      }
    }

    console.log(`Processed attachments: ${finalAttachments.length} attachments, ${downloadLinks.length} download links`);
    
    return { attachments: finalAttachments, downloadLinks };
  }

  /**
   * Helper method to convert File to base64 (bestehende Funktionalität)
   * BESTEHENDE FUNKTIONALITÄT - UNVERÄNDERT BEIBEHALTEN
   */
  async fileToBase64(file: File): Promise<EmailAttachment> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const base64 = reader.result as string;
        // Remove data:*/*;base64, prefix
        const base64Content = base64.split(',')[1];
        resolve({
          filename: file.name,
          content: base64Content,
          contentType: file.type || 'application/octet-stream'
        });
      };
      reader.onerror = error => reject(error);
    });
  }
}
